<template>
  <li class="bookmark-nav">
    <div class="bookmark-wrapper">
      <div class="bookmark-trigger">
        <IconButton
          :title="bookmarkButtonTitle"
          @click="openShortcuts('favorites')"
          :invert-color="invertColor"
        >
          <span class="bookmark-trigger-icon">
            <i :class="isCurrentPageBookmarked ? 'fa fa-star' : 'fa fa-star-o'"></i>
            <span v-if="!isCurrentPageBookmarked" class="bookmark-trigger-badge">+</span>
          </span>
        </IconButton>
      </div>
    </div>

    <Teleport to="body">
    <div
      v-if="showModal"
      class="navigation-shortcuts-modal ryd-modal-backdrop"
      tabindex="-1"
      @click.self="closeModal"
    >
      <div class="navigation-shortcuts-dialog ryd-modal-dialog">
        <div class="nsd-panel">

          <!-- Header -->
          <div class="nsd-header d-flex align-items-start justify-content-between gap-3">
            <div class="nsd-header-left d-flex align-items-center gap-3">
              <span class="nsd-header-icon"><i class="fa fa-compass"></i></span>
              <div>
                <div class="nsd-title">Navigation Shortcuts</div>
                <div class="nsd-subtitle">Jump to any saved or recent page</div>
              </div>
            </div>
            <button type="button" class="nsd-close-btn" aria-label="Close" @click="closeModal">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <!-- Current page strip -->
          <div class="nsd-current-page">
            <div class="nsd-current-page-top d-flex align-items-md-center justify-content-between gap-3 flex-column flex-md-row">
              <div class="nsd-current-page-info d-flex align-items-md-center gap-2 flex-column flex-md-row">
                <span class="nsd-current-page-label">You're here</span>
                <span class="nsd-current-page-title">{{ currentPageDisplay.title }}</span>
              </div>
              <button
                class="nsd-add-btn btn btn-primary btn-sm"
                @click="addCurrentPage"
                :disabled="loading || addingBookmark || isCurrentPageBookmarked"
              >
                <span v-if="loading || addingBookmark" class="spinner-border spinner-border-sm"></span>
                <i v-else class="fa" :class="isCurrentPageBookmarked ? 'fa-check' : 'fa-star-o'"></i> &nbsp;
                <span>{{ currentPageActionLabel }}</span>
              </button>
            </div>
            <span class="nsd-current-page-path">{{ currentPageDisplay.breadcrumbText }}</span>
          </div>

          <!-- Tabs -->
          <div class="nsd-tabs d-flex gap-2">
            <button
              type="button"
              class="nsd-tab"
              :class="{ active: activeTab === 'favorites' }"
              @click="activeTab = 'favorites'"
            >
              <i class="fa fa-star"></i>
              Favorites
              <span class="nsd-tab-badge">{{ bookmarks.length }}</span>
            </button>
            <button
              type="button"
              class="nsd-tab"
              :class="{ active: activeTab === 'recent' }"
              @click="activeTab = 'recent'"
            >
              <i class="fa fa-history"></i>
              Recent
              <span class="nsd-tab-badge">{{ recentItems.length }}</span>
            </button>
          </div>

          <!-- Content -->
          <div class="nsd-body">

            <div v-if="activeTab === 'favorites'">
              <div v-if="loading" class="nsd-loading">
                <i class="fa fa-spinner fa-spin"></i> Loading…
              </div>
              <div v-else-if="bookmarks.length === 0" class="nsd-empty">
                <i class="fa fa-star-o nsd-empty-icon"></i>
                <div class="nsd-empty-title">No favorites yet</div>
                <div class="nsd-empty-hint">Tap the star button above to save this page.</div>
              </div>
              <div v-else class="nsd-list">
                <button
                  v-for="bookmark in bookmarks"
                  :key="bookmark.id"
                  type="button"
                  class="nsd-item d-flex align-items-center gap-3"
                  @click="navigateToBookmark(bookmark)"
                >
                  <span class="nsd-item-icon nsd-item-icon--fav">
                    <i class="fa" :class="bookmark.icon || 'fa-star'"></i>
                  </span>
                  <span class="nsd-item-body d-flex flex-column flex-grow-1">
                    <span class="nsd-item-title">{{ bookmark.title || getDisplayData(bookmark.path).title }}</span>
                    <span class="nsd-item-crumb">{{ getDisplayData(bookmark.path).breadcrumbText }}</span>
                  </span>
                  <i class="fa fa-chevron-right nsd-item-arrow"></i>
                </button>
              </div>
            </div>

            <div v-else>
              <div class="nsd-hint">Showing last 15 recently visited pages</div>
              <div v-if="recentItems.length === 0" class="nsd-empty">
                <i class="fa fa-history nsd-empty-icon"></i>
                <div class="nsd-empty-title">No recent pages</div>
                <div class="nsd-empty-hint">Pages you visit will appear here.</div>
              </div>
              <div v-else class="nsd-list">
                <button
                  v-for="recentItem in recentItems"
                  :key="recentItem.path"
                  type="button"
                  class="nsd-item d-flex align-items-center gap-3"
                  @click="navigateToRecentItem(recentItem)"
                >
                  <span class="nsd-item-icon nsd-item-icon--recent">
                    <i class="fa fa-history"></i>
                  </span>
                  <span class="nsd-item-body d-flex flex-column flex-grow-1">
                    <span class="nsd-item-title">{{ recentItem.title || getDisplayData(recentItem.path).title }}</span>
                    <span class="nsd-item-crumb">{{ getDisplayData(recentItem.path).breadcrumbText }}</span>
                  </span>
                  <i class="fa fa-chevron-right nsd-item-arrow"></i>
                </button>
              </div>
            </div>

          </div>

          <!-- Footer -->
          <div class="nsd-footer d-flex align-items-center justify-content-between gap-3 flex-wrap">
            <router-link to="/bookmarks" class="nsd-manage-link" @click="closeModal">
              <i class="fa fa-cog"></i> Manage Favorites
            </router-link>
            <button type="button" class="nsd-done-btn btn btn-primary" @click="closeModal">Done</button>
          </div>

        </div>
      </div>
    </div>
    </Teleport>
  </li>
</template>

<script>
import { mapState } from 'vuex';
import { useToast } from 'vue-toastification';
import { bookmarkService } from '@/services/bookmarkService';
import IconButton from '@/components/common/IconButton.vue';

const OPEN_SHORTCUTS_EVENT = 'open-navigation-shortcuts';

export default {
  name: 'BookmarkDropdown',
  components: { IconButton },
  props: {
    invertColor: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: false,
      activeTab: 'favorites',
      bookmarks: [],
      recentItems: [],
      loading: false,
      addingBookmark: false,
      toast: null,
      displayDataCache: {},
    };
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    ...mapState({
      menuItems: state => state.menu.data,
    }),
    currentUserData() {
      const userAddDataAsStr = localStorage.getItem('userAddData');
      return userAddDataAsStr ? JSON.parse(userAddDataAsStr) : null;
    },
    recentItemsStorageKey() {
      const uid = this.currentUserData?.uid || this.currentUserData?.id || this.currentUserData?.email || this.currentUserData?.userGroup || 'anonymous';
      return `sidebarRecent:${uid}`;
    },
    isCurrentPageBookmarked() {
      if (!this.$route) return false;
      return this.bookmarks.some(b => b.path === this.$route.path);
    },
    bookmarkButtonTitle() {
      return this.isCurrentPageBookmarked
          ? 'Open navigation shortcuts'
          : 'Open navigation shortcuts and add the current page to favorites';
    },
    currentPageActionLabel() {
      if (this.loading) {
        return 'Checking Favorites...';
      }
      if (this.addingBookmark) {
        return 'Adding Current Page...';
      }
      return this.isCurrentPageBookmarked ? 'Already in Favorites' : 'Add Current Page';
    },
    currentPageDisplay() {
      return this.getDisplayData(this.$route?.path || '/', this.$route?.meta?.title || this.$route?.name || 'Current page');
    },
  },
  mounted() {
    window.addEventListener(OPEN_SHORTCUTS_EVENT, this.handleExternalOpen);
  },
  beforeUnmount() {
    window.removeEventListener(OPEN_SHORTCUTS_EVENT, this.handleExternalOpen);
  },
  methods: {
    handleExternalOpen(event) {
      this.openShortcuts(event?.detail?.tab || 'favorites');
    },
    async openShortcuts(tab = 'favorites') {
      this.activeTab = tab;
      this.showModal = true;
      await this.loadData();
    },
    closeModal() {
      this.showModal = false;
    },
    async loadData() {
      await Promise.all([
        this.loadBookmarks(),
        Promise.resolve(this.loadRecentItems()),
      ]);
    },
    async loadBookmarks() {
      try {
        this.loading = true;
        const response = await bookmarkService.getBookmarks();
        this.bookmarks = response.items || [];
      } catch (error) {
        console.error('[BookmarkDropdown] Error loading bookmarks:', error);
        this.bookmarks = [];
      } finally {
        this.loading = false;
      }
    },
    loadRecentItems() {
      try {
        const storedItems = JSON.parse(localStorage.getItem(this.recentItemsStorageKey) || '[]');
        this.recentItems = Array.isArray(storedItems) ? storedItems.slice(0, 15) : [];
      } catch (error) {
        console.warn('Failed to load recent items for shortcuts modal', error);
        this.recentItems = [];
      }
    },
    getDisplayData(path, fallbackTitle = null) {
      const cacheKey = `${path}:${fallbackTitle || ''}`;
      if (this.displayDataCache[cacheKey]) {
        return this.displayDataCache[cacheKey];
      }

      const found = this.findMenuContextByPath(path);
      let displayData;

      if (found) {
        const breadcrumb = [found.sectionTitle, ...found.chain].filter(Boolean);
        displayData = {
          title: fallbackTitle || breadcrumb[breadcrumb.length - 1] || path,
          breadcrumb,
          breadcrumbText: breadcrumb.join(' -> '),
        };
      } else {
        const fallbackBreadcrumb = this.pathToBreadcrumb(path);
        displayData = {
          title: fallbackTitle || fallbackBreadcrumb[fallbackBreadcrumb.length - 1] || path,
          breadcrumb: fallbackBreadcrumb,
          breadcrumbText: fallbackBreadcrumb.join(' -> '),
        };
      }

      this.displayDataCache[cacheKey] = displayData;
      return displayData;
    },
    pathToBreadcrumb(path) {
      const segments = (path || '/')
          .split('/')
          .filter(Boolean)
          .map(segment => segment.replace(/[-_]/g, ' '))
          .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1));

      return segments.length > 0 ? segments : ['Home'];
    },
    findMenuContextByPath(targetPath) {
      let currentSectionTitle = 'Navigation';

      for (const menuItem of this.menuItems) {
        if (menuItem.type === 'headtitle') {
          currentSectionTitle = this.getTranslation(menuItem.headTitle1 || menuItem.headTitle2 || 'Navigation');
          continue;
        }

        const chain = this.findPathChain(menuItem, targetPath);
        if (chain) {
          return {
            sectionTitle: currentSectionTitle,
            chain,
          };
        }
      }

      return null;
    },
    findPathChain(menuItem, targetPath, parents = []) {
      const currentTitle = menuItem.title ? this.getTranslation(menuItem.title) : null;
      const nextParents = currentTitle ? [...parents, currentTitle] : [...parents];

      if (menuItem.path === targetPath) {
        return nextParents;
      }

      for (const child of menuItem.children || []) {
        const childMatch = this.findPathChain(child, targetPath, nextParents);
        if (childMatch) {
          return childMatch;
        }
      }

      return null;
    },
    getTranslation(key) {
      if (!key) return '';

      const globalKey = `global.${key}`;
      const globalTranslation = this.$t(globalKey);
      return globalTranslation !== globalKey ? globalTranslation : key;
    },
    async addCurrentPage() {
      if (this.isCurrentPageBookmarked) {
        this.toast.info('This page is already in favorites.');
        return;
      }

      try {
        this.addingBookmark = true;
        await bookmarkService.bookmarkCurrentPage(this.$route, this.currentPageDisplay.title);
        this.toast.success('Current page added to favorites.');
        await this.loadBookmarks();
        this.activeTab = 'favorites';
      } catch (error) {
        console.error('[BookmarkDropdown] Error adding bookmark:', error);
        this.toast.error('Failed to add the current page to favorites.');
      } finally {
        this.addingBookmark = false;
      }
    },
    async navigateToBookmark(bookmark) {
      try {
        bookmarkService.trackAccess(bookmark.id);
        if (bookmark.path !== this.$route.path) {
          await this.$router.push(bookmark.path);
        }
        this.closeModal();
      } catch (error) {
        console.error('Error navigating to bookmark:', error);
      }
    },
    async navigateToRecentItem(recentItem) {
      try {
        if (recentItem.path !== this.$route.path) {
          await this.$router.push(recentItem.path);
        }
        this.closeModal();
      } catch (error) {
        console.error('Error navigating to recent item:', error);
      }
    },
  },
};
</script>

<style scoped>
.bookmark-nav {
  position: relative;
}

.bookmark-trigger-icon {
  position: relative;
  display: inline-flex;
}

.bookmark-trigger-badge {
  position: absolute;
  top: -7px;
  right: -10px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  color: #1b1f3b;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
}
</style>
